import { AcfBridge } from "../model/AcfBridge.js";
import { store } from "../model/DataManager.js";
import { Language, LanguageCode } from "../model/Language.js";
import { Resource } from "../model/Resource.js";
import { VOMarker, VOMedia, VOVideo } from "../model/Structure.js";
import { Marker2d } from "./Marker2d.js";
import { PanoButton, PanoButtonOptions } from "./PanoButton.js";
import { View3d } from "./View3d.js";
import { Welcome } from "./Welcome.js";
import { BaseControl, ControlPosition } from "./controls/BaseControl.js";
import { ControlEdit } from "./controls/ControlEdit.js";
import { ControlError } from "./controls/ControlError.js";
import { ControlFullscreen } from "./controls/ControlFullscreen.js";
import { ControlLanguage } from "./controls/ControlLanguage.js";
import { ControlLookAt } from "./controls/ControlLookAt.js";
import { ControlMarkerlist } from "./controls/ControlMarkerlist.js";
import { ControlMinimap } from "./controls/ControlMinimap.js";
import { ControlOpen } from "./controls/ControlOpen.js";
import { ControlSidebar } from "./controls/ControlSidebar.js";
import { ControlTimer } from "./controls/ControlTimer.js";
import { ControlToggle } from "./controls/ControlToggle.js";
import { ControlTrack } from "./controls/ControlTrack.js";
import { ControlVolume } from "./controls/ControlVolume.js";

export interface IPanoOptions {
    debug?: boolean;
}

export class Pano {
    public static DEFAULT: IPanoOptions = {
        debug: false
    };

    public onResize = e5.Signal.create();
    public onChangeMarkerPosition = e5.Signal.create();
    public onChangeCameraFov = e5.Signal.create();
    public onChangeCameraDirection = e5.Signal.create();
    public onChangeVideo = e5.Signal.create<[VOVideo]>();
    public onError = e5.Signal.create<[Error]>();
    public isMobile: boolean = false;
    public element: HTMLElement;

    private _video: HTMLVideoElement;
    private _viewContainer: HTMLElement;
    private _markerContainer: HTMLElement;
    private _markers: Marker2d[] = [];
    private _current: VOVideo = null;
    private _firstVideo = true;
    private _currentPoster: string = "";
    private _view3d: View3d;
    private _options: IPanoOptions;
    private _lastWidth = 0;
    private _lastHeight = 0;
    private _acfBridge: AcfBridge;
    private _currentSource: VOMedia = null;
    private _history: e5.History;

    constructor(container: HTMLElement, options: IPanoOptions = {}) {
        console.log("[eventfive.awi.pano] [Pano]");
        this.init(container, options);
    }

    private async init(container: HTMLElement, options: IPanoOptions = {}) {
        this.element = container;
        this.element.innerHTML = `
        <div class="pano__wrapper">
            <div class="pano__video-container">
                <canvas class="three-canvas"></canvas>
                <div class="marker-container"></div>
            </div>           
            <div class="pano__control-overlay" data-control="overlay"></div>
            <div class="pano__control">
                <div class="pano__control-buttons">
                    <div class="pano__control-buttons-group pano__control-buttons-group--left" data-control="left"></div>
                    <div class="pano__control-buttons-group pano__control-buttons-group--center" data-control="center"></div>
                    <div class="pano__control-buttons-group pano__control-buttons-group--right" data-control="right"></div>
                </div>
                <div class="pano__control-seek" data-control="seek"></div>
            </div>
        </div>
        `;
        this._viewContainer = container.querySelector(".pano__video-container");
        this._markerContainer = container.querySelector(".marker-container");

        new e5.Tooltip({
            touchSupport: false,
            scope: this.element
        });

        const lang = e5.Url.params.get("language") || e5.Url.params.get("lang") || "de";
        Language.init(lang as LanguageCode);

        const video = await store.load(
            // "sdfsdfsd",
            container.getAttribute("data-dataurl"),
            parseInt(this.element.getAttribute("data-video")));

        if (!video) {
            return;
        }

        if (store.config.welcome) {
            const w = new Welcome({
                container: this.element
            });
            w.open(store.config.welcome);
        }

        Resource.init(container);
        if (container.hasAttribute("data-debug")) {
            options.debug = true;
        }
        this._options = e5.object.merge(Pano.DEFAULT, options);
        this.isMobile = e5.Caps.isMobile;

        this._video = document.createElement('video');
        this._video.crossOrigin = "anonymous";
        this._video.className = "video";
        this._video.autoplay = false;
        this._video.setAttribute("webkit-playsinline", "webkit-playsinline");
        this._video.setAttribute("playsinline", "playsinline");

        this._view3d = new View3d(this);
        this._view3d.visible = false;

        //Add controls.
        this.addItem(new ControlTrack(this));
        this.addItem(new ControlToggle(this));
        if (!this.options.debug) {
            // this.addItem(new ControlNext(this));
        }
        this.addItem(new ControlVolume(this));
        this.addItem(new ControlTimer(this));
        if (store.config.show_language_switcher) {
            this.addItem(new ControlLanguage(this));
        }
        if (!this.options.debug) {
            this.addItem(new ControlMarkerlist(this))
        }
        if (!this.options.debug) {
            this.addItem(new ControlSidebar(this));
        }
        if (!this.options.debug
            && e5.Plugins.user?.exists()) {
            this.addItem(new ControlEdit(this));
        }
        if (this.options.debug) {
            this.addItem(new ControlOpen(this));
            this.addItem(new ControlLookAt(this));
        }
        if (e5.FullscreenUtil.isSupported) {
            this.addItem(new ControlFullscreen(this));
        }
        this.addItem(new ControlMinimap(this, this._view3d));
        this.addItem(new ControlError(this));

        this.current = video;

        this.resize();
        this.animate();
        this._view3d.renderer.setAnimationLoop(() => this.animate());

        if (!this.options.debug) {
            this._history = new e5.History();
            this._history.onHashChange.add((hash) => {
                this.parseHash();
            });
        }

        if (this._options.debug) {
            this._acfBridge = new AcfBridge(this);
        }

        this._video.addEventListener("play", () => {
            this.hidePoster();
        });
        this._video.addEventListener("ended", () => {
            if (!this._acfBridge && this._current && this._current.next_video) {
                this.load(this._current.next_video, "end");
            }
        });
    }

    private parseHash(): void {
        if (!this._history || !store.data)
            return;

        var hash = this._history.getHash();
        var spl = hash.split("/");
        this.load(parseInt(spl[1]), "hash");
    }

    public get video(): HTMLVideoElement {
        return this._video;
    }

    public get viewContainer(): HTMLElement {
        return this._viewContainer;
    }

    public get overlay(): HTMLElement {
        return this.element.querySelector(`[data-control="overlay"]`);
    }

    public get options(): IPanoOptions {
        return this._options;
    }

    public get acfBridge(): AcfBridge {
        return this._acfBridge;
    }

    public get markers(): Marker2d[] {
        return this._markers;
    }

    public load(id: number, from: string = ""): void {
        var d = store.getVideo(id);
        this.current = d;
        // console.log("Load video from: " + from);
        if (!d)
            this.showError(new Error("Could not load video!"));
    }

    public get current(): VOVideo {
        return this._current;
    }

    public set current(video: VOVideo) {
        if (this._current == video && !this.options.debug)
            return;
        this.hidePoster();

        this._current = video;

        if (video) {
            if (this._history)
                this._history.setHash("video/" + video.id);

            this._view3d.visible = true;

            var source = this.getVideo();
            var currentUrl = this._currentSource ? this._currentSource.url : null;
            var url = source ? source.url : null;
            this._currentSource = source;

            this._video.loop = this.options.debug ? true : !Boolean(video.next_video);
            this._video.autoplay = false;
            this._video.dispatchEvent(new Event("volumechange"));
            this._video.setAttribute("data-src", url);
            this._video.preload = "metadata";

            var autoplay = !this._firstVideo;

            if (this._options.debug) {
                autoplay = false;
            }

            //The user already interacted with the dom.
            //So we can autoplay the video.
            //Or there is no poster given, than we have to pre-load the video.
            if (autoplay || !this.getPoster()) {
                this._video.removeAttribute("src");

                /**
                 * Use first frame as poster.
                 */
                if (!this.getPoster()) {
                    url += "#t=0.01";
                }

                this._video.src = url;
                this._video.load();

                if (autoplay) {
                    var playPromise = this._video.play();
                    if (playPromise !== undefined) {
                        playPromise.then((v) => {

                        }).catch((err) => {
                            this.showPoster();
                        });
                    }
                }
            }
            //Its the first video we load and there is a poster for it.
            else if (this.getPoster()) {
                this.showPoster();
            }


            this._firstVideo = false;

            this.setMarkers(video.markers);
        }
        else {
            this._view3d.visible = false;
            this.setMarkers(null);
            this._video.removeAttribute("src");
            this._video.load();

            this.showError(new Error("Video not found!"));
        }

        this.onChangeVideo.dispatch(video);

        Language.onChange.add(() => {
            if (this.current)
                this.setMarkers(this.current.markers);
        });
    }

    private getVideo(): VOMedia {
        if (!this._current)
            return null;
        var ordered = this.getVideoMediaOrdered();
        if (!ordered)
            return null;
        return this.fallback.apply(null, ordered);
    }

    public fallback(...items: any[]): any {
        for (var i = 0; i < items.length; ++i) {
            if (items[i])
                return items[i];
        }
        return null;
    }

    private getPoster(): string {
        var ordered = this.getVideoMediaOrdered();
        for (var media of ordered) {
            if (media && media.sizes && media.sizes.full)
                return media.sizes.full;
        }


        for (var media of ordered) {
            if (media && media.sizes && media.sizes["2048x2048"])
                return media.sizes["2048x2048"];
        }
        return null;
    }

    private getVideoMediaOrdered(): VOMedia[] {
        if (!this._current)
            return null;
        var c = this._current;
        if (Language.current === "de") {
            if (this.isMobile)
                return [c.video_mobile_de, c.video_mobile, c.video_desktop_de, c.video_desktop];
            else
                return [c.video_desktop_de, c.video_desktop, c.video_mobile_de, c.video_mobile];
        }
        else if (Language.current === "en") {
            if (this.isMobile)
                return [c.video_mobile, c.video_mobile_de, c.video_desktop, c.video_desktop_de];
            else
                return [c.video_desktop, c.video_desktop_de, c.video_mobile, c.video_mobile_de];
        }
        return null;
    }

    private setMarkers(datas: VOMarker[]): void {
        for (var m of this._markers) {
            m.element.parentElement.removeChild(m.element);
        }
        this._markers = [];

        var isHidden = this._markerContainer.classList.contains("hidden");
        this._markerContainer.classList.remove("hidden");

        if (datas) {
            for (var data of datas) {
                var marker = new Marker2d(data, this);
                this._markers.push(marker);
                this._markerContainer.appendChild(marker.element);
                marker.width = marker.content.offsetWidth;
                marker.height = marker.content.offsetHeight;
                marker.visible = false;
            }
            this.updateMarkers();
        }

        this._markerContainer.classList.toggle("hidden", isHidden);
    }

    public showMarkers(show: boolean): void {
        this._markerContainer.classList.toggle("hidden", !show);
        if (show)
            this.updateMarkers();
    }

    private updateMarkers(): void {
        if (!this._current || this._markerContainer.classList.contains("hidden"))
            return;

        var time = this._video.currentTime;
        for (var marker of this._markers) {
            var data = marker.data;

            var visible = true;

            //Check time
            if (data.marker_start_time != -1 && data.marker_end_time != -1) {
                visible = time >= data.marker_start_time && time <= data.marker_end_time;
            }
            else if (data.marker_start_time != -1) {
                visible = time >= data.marker_start_time;
            }
            else if (data.marker_end_time != -1) {
                visible = time <= data.marker_end_time;
            }

            data.isInTime = visible;
            for (var delegate of data.delegates) {
                delegate.visible = visible;
            }

            if (visible) {
                var loc = this.view.getMarkerPosition(marker);
                if (loc && loc.isInView) {
                    marker.element.style.transform = "translate(" + loc.x + "px, " + loc.y + "px)";
                    visible = true;
                }
                else {
                    visible = false;
                }
            }

            marker.visible = visible;
        }
    }

    public play(): void {
        if (!this._video.src)
            this._video.src = this._video.getAttribute("data-src");
        this._video.play();
    }

    public get view(): View3d {
        return this._view3d;
    }

    public addItem<T extends BaseControl>(control: T): T {
        return control;
    }

    public addControl(control: HTMLElement, position: ControlPosition): HTMLElement {
        this.element.querySelector(`[data-control="${position}"]`).appendChild(control);
        return control;
    }

    public addButton(control: PanoButtonOptions): PanoButton {
        var btn = new PanoButton(control);
        this.element.querySelector(`[data-control="${control.position}"]`).appendChild(btn.element);
        return btn;
    }

    public showError(message: Error): void {
        this.onError.dispatch(message);
    }

    private showPoster(): boolean {
        var posterUrl = this.getPoster();
        if (this._currentPoster == posterUrl)
            return false;

        if (!posterUrl)
            return false;
        this._currentPoster = posterUrl;

        this.view.showPoster(posterUrl);
        this.element.classList.add("shows-poster");
        return true;
    }

    private hidePoster(): void {
        if (!this._currentPoster)
            return;
        this._currentPoster = null;
        this.view.hidePoster();
        this.element.classList.remove("shows-poster");
    }

    private resize(): void {
        // this._timer.visible = true;
        // var bnds = this._viewContainer.getBoundingClientRect();
        // var left = this.element.querySelector(`.pano__control-buttons-group--left`) as HTMLElement;
        // var right = this.element.querySelector(`.pano__control-buttons-group--right`) as HTMLElement;
        // this._timer.visible = left.offsetWidth + right.offsetWidth + 25 < bnds.width;
        this.onResize.dispatch();
    }

    private animate(): void {
        var bnds = this._viewContainer.getBoundingClientRect();
        if (bnds.width != this._lastWidth || bnds.height != this._lastHeight) {
            this._lastWidth = bnds.width;
            this._lastHeight = bnds.height;
            this.resize();
        }
        this.view.update();
        this.updateMarkers();
    }
}